"use client";
import dynamic from "next/dynamic";
import type { BlockData, Post } from "../types";
import { BlockProvider, setIn, type AttrPath } from "./context";
import { getBlockDefs } from "./registry";

interface Props {
  themeId?: string;
  areaDepth?: number;
  preview?: boolean;
  blocks: BlockData[];
  blockData: Record<string, unknown>;
  locale: string;
  locales: string[];
  localeDomains?: Record<string, string>;
  localeAlternates?: Record<string, string>;
  enabledModules: string[];
  editing?: boolean;
  blockIndexOffset?: number;
  onChange?: (blocks: BlockData[]) => void;
  post?: Post;
}

// Builder-only chunks — never loaded on the public site.
const BlockChrome = dynamic(() => import("./BlocksView.editable").then((m) => ({ default: m.BlockChrome })), { ssr: false });
const EndInsert = dynamic(() => import("./BlocksView.editable").then((m) => ({ default: m.EndInsert })), { ssr: false });
const MissingBlock = dynamic(() => import("./BlocksView.editable").then((m) => ({ default: m.MissingBlock })), { ssr: false });

/** Renders a page's blocks. Same component powers the site and the builder. */
export function BlocksView(props: Props) {
  const defs = getBlockDefs(props.themeId, props.enabledModules);
  const editing = !!props.editing;

  const update = (i: number, path: AttrPath, value: unknown) => {
    if (!props.onChange) return;
    const next = [...props.blocks];
    next[i] = { ...next[i], attrs: setIn(next[i].attrs || {}, path, value) as Record<string, unknown> };
    props.onChange(next);
  };

  return (
    <>
      {props.blocks.map((block, i) => {
        const def = defs.find((d) => d.type === block.type);
        if (!def) return editing ? <MissingBlock key={block.id} type={block.type} /> : null;

        const content = (
          <BlockProvider
            value={{
              themeId: props.themeId,
              areaDepth: props.areaDepth,
              preview: props.preview,
              attrs: block.attrs || {},
              data: props.blockData[block.id],
              locale: props.locale,
              locales: props.locales,
              localeDomains: props.localeDomains,
              localeAlternates: props.localeAlternates,
              enabledModules: props.enabledModules,
              editing,
              blockIndex: i + (props.blockIndexOffset ?? 0),
              scope: [],
              setAttr: (path, value) => update(i, path, value),
              post: props.post,
            }}
          >
            <def.component />
          </BlockProvider>
        );

        if (!editing) return <div key={block.id}>{content}</div>;
        return (
          <BlockChrome
            key={block.id}
            index={i}
            block={block}
            blocks={props.blocks}
            def={def}
            defs={defs}
            themeId={props.themeId || ""}
            enabledModules={props.enabledModules}
            locale={props.locale}
            locales={props.locales}
            onChange={props.onChange}
          >
            {content}
          </BlockChrome>
        );
      })}
      {editing && <EndInsert themeId={props.themeId || ""} enabledModules={props.enabledModules} defs={defs} blocks={props.blocks} onChange={props.onChange} />}
    </>
  );
}
